import React, { useState, useEffect } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { getAbout } from '../api/api';
import { useAppearance } from '../contexts/AppearanceContext';

export const About = () => {
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const { appearance } = useAppearance();

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const data = await getAbout();
        setAbout(data);
      } catch (error) {
        console.error('Error fetching about:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAbout();
  }, []);

  if (loading || !about) {
    return null;
  }

  const paragraphs = about.content.split('\n\n');
  // Image de la section « À propos » : pilotée depuis le panel (Apparence),
  // avec un repli neutre si aucune image n'est renseignée.
  const aboutImage = appearance.about_image || "https://images.unsplash.com/photo-1497366858526-0766cadbe8fa?auto=format&fit=crop&w=1000&q=80";

  return (
    <section id="about" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Content */}
          <div>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-gray-900 mb-6">
              {about.title}
            </h2>

            {paragraphs.map((paragraph, index) => (
              <p key={index} className="text-lg text-gray-600 leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}

            {/* Key Points */}
            <div className="space-y-4 mt-8">
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">30 ans de métier</h4>
                  <p className="text-gray-600">Une expertise reconnue dans le secteur du nettoyage professionnel</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Approche personnalisée</h4>
                  <p className="text-gray-600">Des solutions adaptées à vos besoins spécifiques</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Respect de l'environnement</h4>
                  <p className="text-gray-600">Utilisation de produits écologiques et techniques modernes</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Images */}
          <div className="relative">
            {/* Main Image */}
            <div className="relative z-10 rounded-3xl overflow-hidden shadow-lg">
              <img
                loading="lazy"
                decoding="async"
                width="800"
                height="600"
                src={aboutImage}
                alt="NETTO+ — nettoyage professionnel"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 to-transparent"></div>
            </div>

            {/* Floating Card */}
            <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-lg p-6 z-20 max-w-xs">
              <div className="flex items-center space-x-4">
                <div className="bg-blue-600 rounded-xl p-4">
                  <CheckCircle2 className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">500+</div>
                  <div className="text-sm text-gray-600">Clients satisfaits</div>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br from-blue-200 to-green-200 rounded-full blur-3xl opacity-60"></div>
            <div className="absolute -bottom-12 -right-12 w-40 h-40 bg-gradient-to-br from-green-200 to-blue-200 rounded-full blur-3xl opacity-60"></div>
          </div>
        </div>
      </div>
    </section>
  );
};
